import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async"; 
import {
  Shield,
  Lock,
  Eye,
  Cookie,
  Globe,
  Mail,
  Users,
  CheckSquare,
  Scale,
  FileText,
  Gavel,
  AlertCircle,
  UserX
} from "lucide-react";
import Aos from "aos";
import "aos/dist/aos.css";
import { useSEO } from "../Pages/Seo";

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    Aos.init({ duration: 500, once: true });
  }, []);

  useSEO({
    title: "Terms and Conditions | Game Zone Hub",
    description: "Read the formal Terms and Conditions governing your use of Game Zone Hub. This document outlines user obligations, intellectual property rights, and platform usage policies.",
    keywords: "terms of service, legal agreement, game zone hub terms, usage policy, intellectual property rights",
    canonical: "https://www.gamezonehub.in/terms",
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms and Conditions - Game Zone Hub",
    "description": "Formal Terms of Service governing the use of Game Zone Hub.",
    "url": "https://www.gamezonehub.in/terms"
  };

  const sections = [
    {
      id: "acceptance",
      title: "1. Agreement to Terms",
      icon: <FileText className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            By accessing or using the Game Zone Hub platform, you formally agree to be bound by these Terms and Conditions. These terms constitute a legally binding agreement between you ("User") and Game Zone Hub. If you do not agree with any provision contained within this document, you are strictly prohibited from accessing our services.
          </p>
          <p>
            The user represents and warrants that they possess the legal capacity to enter into this agreement. Access by minors under the age of 13 is strictly prohibited without the direct supervision and explicit consent of a parent or legal guardian.
          </p>
        </div>
      ),
    },
    {
      id: "intellectual",
      title: "2. Intellectual Property Rights",
      icon: <Shield className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            The Game Zone Hub platform, including its proprietary source code, interface designs, trade names, and branding, is the exclusive intellectual property of Game Zone Hub and is protected under international copyright and trademark legislation.
          </p>
          <p>
            The digital games hosted on our platform are the property of their respective developers and publishers. Game Zone Hub maintains valid licenses to host and display these assets. Unauthorized reproduction, modification, or commercial exploitation of any material found on this platform is strictly prohibited.
          </p>
        </div>
      ),
    },
    {
      id: "user-conduct",
      title: "3. Acceptable Use Policy",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Users are strictly prohibited from engaging in any activity that compromises the integrity of our platform. Prohibited actions include, but are not limited to:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-400 marker:text-cyan-500">
            <li>Deployment of automated bots, scripts, or scraping tools.</li>
            <li>Manipulation of game leaderboards, scores, or ranking systems.</li>
            <li>Any attempt to disrupt, impair, or gain unauthorized access to our server infrastructure or security protocols.</li>
            <li>Transmitting any content that is defamatory, obscene, or infringing upon the rights of others.</li>
          </ul>
        </div>
      ),
    },
    {
      id: "third-party",
      title: "4. External Links and Third-Party Assets",
      icon: <Globe className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Game Zone Hub may contain links to third-party domains. These links are provided for convenience only and do not constitute an endorsement. We assume no liability for the content, privacy policies, or operational standards of external websites. Accessing such links is entirely at the User's sole discretion and risk.
          </p>
        </div>
      ),
    },
    {
      id: "termination",
      title: "5. Suspension of Access",
      icon: <UserX className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We reserve the right, at our absolute discretion, to terminate or restrict access to any User who violates these Terms and Conditions. Such termination may be executed without prior notification. We further reserve the right to report malicious activity to relevant internet service providers or regulatory authorities where legally required.
          </p>
        </div>
      ),
    },
    {
      id: "disclaimer",
      title: "6. Limitation of Liability",
      icon: <AlertCircle className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p className="font-bold text-white uppercase">Disclaimer: "As-Is" Service</p>
          <p>
            The Game Zone Hub platform and all hosted content are provided on an "as-is" and "as-available" basis. We provide no warranties, express or implied, regarding the continuous availability, accuracy, or reliability of our services. Under no circumstances shall Game Zone Hub, its affiliates, or its partners be held liable for any direct, indirect, or consequential damages resulting from the use or inability to use our platform.
          </p>
        </div>
      ),
    },
    {
      id: "governing-law",
      title: "7. Modifications to Terms",
      icon: <Gavel className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We reserve the right to modify these Terms and Conditions at any time. Any revisions will be effective immediately upon posting. Your continued use of the website following the publication of any modifications constitutes your explicit acceptance of the revised Terms. It is the responsibility of the user to review this document periodically for updates.
          </p>
        </div>
      ),
    },
    {
      id: "contact", 
      title: "8. Legal Contact", 
      icon: <Mail className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            For any legal inquiries, questions regarding our Terms and Conditions, or to report violations, please reach out to our administrative team at:
          </p>
          <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10 inline-block">
            <a href="mailto:support@gamezonehub.in" className="text-blue-400 font-bold hover:underline">support@gamezonehub.in</a>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="relative bg-[#0b1120] min-h-screen text-gray-300 sm:pt-[120px] pt-[100px] pb-11">
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      
      <div className="container mx-auto px-4 relative z-10">
        
        <div className="text-center md:mb-20 mb-11">
          <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold mb-3 text-white" data-aos="fade-down">
            Terms & <span className="text-blue-500">Conditions</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto" data-aos="fade-up">
            Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 max-w-7xl mx-auto">
          <div className="lg:w-1/3">
            <div className="sticky top-28 bg-[#111827]/80 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-lg" data-aos="fade-right">
              <h3 className="text-white font-bold text-xl mb-4 border-b border-white/10 pb-3">Table of Contents</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="group flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
                  >
                    <span className="text-blue-500">{section.icon}</span>
                    <span className="text-sm font-medium">{section.title.replace(/^\d\.\s/, '')}</span>
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:w-2/3 space-y-8">
            {sections.map((section) => (
              <div
                key={section.id}
                id={section.id}
                data-aos="fade-up"
                className="bg-[#111827] rounded-2xl p-6 sm:p-8 border border-white/5 scroll-mt-32 transition-all hover:border-blue-500/30"
              >
                <div className="flex items-center gap-3 mb-5 border-b border-white/5 pb-4">
                  <div className="p-3 bg-blue-500/10 rounded-xl text-blue-400">{section.icon}</div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-gray-300 leading-relaxed text-sm sm:text-base">{section.content}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;